import React, { useState, useEffect, useRef } from 'react';

const UrduKeyboard = ({ onKeyPress, onClose, isVisible, inputRef }) => {
  const [isShift, setIsShift] = useState(false);
  const keyboardRef = useRef(null);
  const [keyboardPosition, setKeyboardPosition] = useState('right'); // 'left' or 'right'

  // Calculate keyboard position based on input field position
  useEffect(() => {
    if (!isVisible || !inputRef?.current) return;

    const inputElement = inputRef.current;
    const inputRect = inputElement.getBoundingClientRect();
    const inputCenterX = inputRect.left + inputRect.width / 2;
    const screenWidth = window.innerWidth;
    const screenCenterX = screenWidth / 2;

    // If input is on right side of screen, show keyboard on left side
    // If input is on left side of screen, show keyboard on right side
    if (inputCenterX > screenCenterX) {
      // Field is on right side → keyboard on left
      setKeyboardPosition('left');
    } else {
      // Field is on left side → keyboard on right
      setKeyboardPosition('right');
    }
  }, [isVisible, inputRef]);

  // Close keyboard when clicking outside
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event) => {
      if (!keyboardRef.current) return;
      
      const target = event.target;
      
      // Don't close if clicking on the keyboard itself
      if (keyboardRef.current.contains(target)) {
        return;
      }
      
      // Don't close if clicking on an input field or textarea
      // This allows the input's onClick handler to work first
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }
      
      // Close keyboard if click is outside
      onClose();
    };

    // Use a small delay to avoid immediate closing when opening the keyboard
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Urdu keyboard layout - Standard Urdu QWERTY layout
  const urduKeys = [
    ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج', 'چ'],
    ['ش', 'س', 'ی', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ک', 'گ', 'ط'],
    ['ظ', 'ز', 'ر', 'ذ', 'د', 'پ', 'و', 'ئ', 'ء', 'آ', 'أ', 'إ'],
    ['ة', 'ى', 'ي', 'لا', ' ', '۔', '،', '؟', '!', '٬', '٭', '؍']
  ];

  const urduKeysShift = [
    ['ٹ', 'ڈ', 'ڑ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' '],
    [' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' '],
    [' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' '],
    [' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ']
  ];

  const handleKeyClick = (key) => {
    if (key === 'Shift') {
      setIsShift(!isShift);
    } else if (key === 'Backspace') {
      onKeyPress('backspace');
    } else if (key === 'Space') {
      onKeyPress(' ');
    } else if (key === 'Enter') {
      onKeyPress('\n');
    } else if (key === 'Close') {
      onClose();
    } else {
      onKeyPress(key);
      // Auto turn off shift after typing
      if (isShift && key !== 'Shift') {
        setIsShift(false);
      }
    }
  };

  if (!isVisible) return null;

  const currentKeys = isShift ? urduKeysShift : urduKeys;

  // Position styles based on input field position
  const positionStyles = keyboardPosition === 'left' 
    ? { 
        bottom: '20px', 
        left: '20px', 
        right: 'auto',
        maxWidth: '400px',
        width: 'auto'
      }
    : { 
        bottom: '20px', 
        right: '20px', 
        left: 'auto',
        maxWidth: '400px',
        width: 'auto'
      };

  return (
    <div 
      ref={keyboardRef} 
      className="fixed bg-white border-2 border-gray-300 shadow-2xl z-[9999] rounded-lg"
      style={{ 
        ...positionStyles,
        maxHeight: '50vh', 
        overflowY: 'auto'
      }}
    >
      {/* Header */}
      <div className="p-2 bg-gray-100 flex justify-between items-center border-b border-gray-300 rounded-t-lg">
        <button
          onClick={onClose}
          className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-semibold transition-colors"
        >
          بند کریں
        </button>
        <span className="text-xs font-semibold text-gray-700" style={{ fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" }}>اردو کی بورڈ</span>
        <div className="w-16"></div> {/* Spacer for centering */}
      </div>
      
      {/* Keyboard Keys */}
      <div className="p-2 space-y-1">
        {currentKeys.map((row, rowIndex) => (
          <div key={rowIndex} className="flex gap-1 justify-center">
            {row.map((key, keyIndex) => (
              <button
                key={keyIndex}
                onClick={() => handleKeyClick(key)}
                className="flex-1 min-w-[25px] h-8 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 border border-gray-300 rounded text-sm font-urdu font-semibold transition-all duration-150 shadow-sm"
                style={{ fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {key}
              </button>
            ))}
          </div>
        ))}
        
        {/* Control buttons row */}
        <div className="flex gap-1 justify-center mt-1.5">
          <button
            onClick={() => handleKeyClick('Shift')}
            onMouseDown={(e) => e.preventDefault()}
            className={`px-3 py-1.5 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 border border-gray-300 rounded text-xs font-semibold transition-all duration-150 ${isShift ? 'bg-blue-400 text-white' : ''}`}
          >
            Shift
          </button>
          <button
            onClick={() => handleKeyClick('Space')}
            onMouseDown={(e) => e.preventDefault()}
            className="flex-1 px-3 py-1.5 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 border border-gray-300 rounded text-xs font-semibold transition-all duration-150"
          >
            Space
          </button>
          <button
            onClick={() => handleKeyClick('Backspace')}
            onMouseDown={(e) => e.preventDefault()}
            className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 border border-gray-300 rounded text-sm font-semibold transition-all duration-150"
          >
            ⌫
          </button>
          <button
            onClick={() => handleKeyClick('Enter')}
            onMouseDown={(e) => e.preventDefault()}
            className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 border border-gray-300 rounded text-xs font-semibold transition-all duration-150"
          >
            Enter
          </button>
        </div>
      </div>
    </div>
  );
};

export default UrduKeyboard;

